import { useLocalization } from '@lib/localization';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { Clock } from 'lucide-react';
import { memo, useMemo } from 'react';

interface DailyInspectionPoint {
  date: Date;
  total: number;
};

interface Props {
  data: DailyInspectionPoint[];
  height?: number;
  color?: string;
  labelColor?: string;
  order?: 'asc' | 'desc';
};

const DEFAULT_CHART_COLOR = '#2196f3';
const DEFAULT_LABEL_COLOR = 'rgba(255,255,255,0.7)';

const InspectionActivityChart = memo(function InspectionActivityChart({
  data,
  height = 336,
  color = DEFAULT_CHART_COLOR,
  labelColor = DEFAULT_LABEL_COLOR,
  order = 'asc',
}: Props) {
  const theme = useTheme();

  const { t, currentLanguage } = useLocalization();

  const formattedData = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return arr
      .map((d) => ({ ...d, date: new Date(d.date) }))
      .sort((a, b) =>
        order === 'asc' ? a.date.getTime() - b.date.getTime() : b.date.getTime() - a.date.getTime()
      );
  }, [data, order]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          px: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Clock size={24} />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
            {t('Statistic_InspectionActivityTimeline')}
          </Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          p: 3,
          height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <LineChart
          height={height - 86}
          dataset={formattedData}
          yAxis={[
            {
              tickMinStep: 1
            },
          ]}
          xAxis={[
            {
              dataKey: 'date',
              scaleType: 'time',
              valueFormatter: (value, context) => {
                const date = new Date(value);
                if (context.location === 'tick') {
                  return date.toLocaleDateString(currentLanguage, {
                    day: '2-digit',
                    month: 'short',
                  });
                }
                return date.toLocaleDateString(currentLanguage, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                });
              },
            },
          ]}
          series={[
            {
              dataKey: 'total',
              label: t('Statistic_InspectionCount'),
              color,
              showMark: true,
              curve: 'monotoneX',
            },
          ]}
          grid={{ horizontal: true, vertical: false }}
          margin={{ left: 0, right: 0, top: 12, bottom: 0 }}
          sx={{
            '--Charts-axis-label-color': labelColor,
            '& .MuiLineElement-root': { strokeWidth: 2 },
            '& .MuiMarkElement-root': {
              r: 4,
              stroke: color,
              fill: theme.palette.background.paper,
            },
          }}
          slotProps={{
            tooltip: {
              sx: {
                '*': {
                  color: '#fff !important',
                },
              },
            },
          }}
        />
      </Paper>
    </Box>
  );
});

export default InspectionActivityChart;
